import { useEffect, useMemo, useState } from "react";
import { v4 } from "uuid";
import { Pencil, Plus, Save, Trash2, X } from "lucide-react";

import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";

import {
  useQpmCertificateHttpCreateMutation,
  useQpmCertificateHttpDeleteMutation,
  useQpmCertificateHttpListQuery,
  useQpmCertificateHttpUpdateMutation,
} from "@/services/backend/qpm/certificates/api/certificate-api";
import type {
  CreateRequest,
  ListItem,
  UpdateRequest,
} from "@/services/backend/qpm/certificates/types/certificate";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";

type Draft = {
  name: string;
  alwaysDisplay: boolean;
};

export function CertificateListTable() {
  const { companyId } = useActiveCompany();
  const {
    data: list,
    error,
    isLoading,
  } = useQpmCertificateHttpListQuery({
    company: { active: true, values: [companyId] },
  });

  const [create, createState] = useQpmCertificateHttpCreateMutation();
  const [update, updateState] = useQpmCertificateHttpUpdateMutation();
  const [remove, removeState] = useQpmCertificateHttpDeleteMutation();

  const items: ListItem[] = useMemo(() => list?.items ?? [], [list?.items]);

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => a.name.localeCompare(b.name)),
    [items],
  );

  const [newRowId, setNewRowId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  const isCreatingNew = editingId != null && editingId === newRowId;

  const [draft, setDraft] = useState<Draft>({ name: "", alwaysDisplay: false });

  useEffect(() => {
    if (!editingId) return;

    if (newRowId && editingId === newRowId) {
      setDraft({ name: "", alwaysDisplay: false });
      return;
    }

    const row = sortedItems.find((c) => c.id === editingId);
    if (!row) return;

    setDraft({ name: row.name, alwaysDisplay: row.alwaysDisplay });
  }, [editingId, newRowId, sortedItems]);

  const onCancel = () => {
    setEditingId(null);
    setNewRowId(null);
    setDraft({ name: "", alwaysDisplay: false });
  };

  const validate = (d: Draft): string | null => {
    if (!d.name || d.name.trim().length === 0) return t("Name fehlt");
    return null;
  };

  const onSave = async () => {
    const validationError = validate(draft);
    if (validationError) return;

    if (isCreatingNew) {
      await create({
        companyId,
        name: draft.name,
        alwaysDisplay: draft.alwaysDisplay,
      } satisfies CreateRequest).unwrap();
      onCancel();
      return;
    }

    if (!editingId) return;

    await update({
      id: editingId,
      name: draft.name,
      alwaysDisplay: draft.alwaysDisplay,
    } satisfies UpdateRequest).unwrap();
    onCancel();
  };

  if (error) return <RTKQueryErrorAlert error={error} />;
  if (isLoading) return <Skeleton className="h-20 w-full" />;

  const canAddRow = newRowId == null && editingId == null;

  return (
    <div className="flex flex-col gap-3">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("Name")}</TableHead>
            <TableHead className="w-[4%] text-right">
              {t("Bearbeiten")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedItems.map((it) => (
            <CertificateRow
              key={it.id}
              item={it}
              isEditMode={editingId === it.id}
              isNew={false}
              draft={draft}
              setDraft={setDraft}
              onEdit={() => setEditingId(it.id)}
              onCancel={onCancel}
              onSave={onSave}
              onDelete={async () => {
                await remove({ id: it.id }).unwrap();
              }}
            />
          ))}

          {newRowId && editingId === newRowId && (
            <CertificateRow
              key={newRowId}
              item={{
                id: newRowId,
                name: draft.name,
                alwaysDisplay: draft.alwaysDisplay,
              }}
              isEditMode
              isNew
              draft={draft}
              setDraft={setDraft}
              onEdit={() => setEditingId(newRowId)}
              onCancel={onCancel}
              onSave={onSave}
              onDelete={undefined}
            />
          )}

          {sortedItems.length === 0 && !newRowId && (
            <TableRow>
              <TableCell colSpan={3} className="text-muted-foreground py-6">
                {t("Keine Einträge gefunden.")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <div className="flex justify-start">
        <Button
          disabled={!canAddRow}
          onClick={() => {
            const id = v4();
            setNewRowId(id);
            setEditingId(id);
            setDraft({ name: "", alwaysDisplay: false });
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          {t("Zertifikat hinzufügen")}
        </Button>
      </div>

      {(createState.error || updateState.error || removeState.error) && (
        <RTKQueryErrorAlert
          error={(createState.error || updateState.error || removeState.error)!}
        />
      )}
    </div>
  );
}

function CertificateRow({
  item,
  isEditMode,
  isNew,
  draft,
  setDraft,
  onEdit,
  onCancel,
  onSave,
  onDelete,
}: {
  item: ListItem;
  isEditMode: boolean;
  isNew: boolean;
  draft: Draft;
  setDraft: (d: Draft) => void;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onDelete?: () => void;
}) {
  if (isEditMode) {
    return (
      <TableRow>
        <TableCell className="py-1.5">
          <Input
            value={draft.name}
            placeholder={t("Name")}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
        </TableCell>
        <TableCell className="py-1.5 text-right">
          <div className="flex items-center justify-end">
            <TooltipProvider>
              {!isNew && onDelete && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="mr-1"
                      size="sm"
                      variant="outline"
                      onClick={onDelete}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t("Löschen")}</TooltipContent>
                </Tooltip>
              )}

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    className="mr-1"
                    size="sm"
                    variant="outline"
                    onClick={onCancel}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("Abbrechen")}</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button size="sm" variant="default" onClick={onSave}>
                    <Save className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("Speichern")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell className="py-1.5">{item.name}</TableCell>
      <TableCell className="py-1.5 text-right">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="sm" onClick={onEdit}>
                <Pencil className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("Bearbeiten")}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
    </TableRow>
  );
}
